import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  QUEUE_SORT_FIELDS,
  SORT_DIRECTIONS,
  isValidYmd,
  type QueueSortField,
} from "@contractorhub/shared";
import { TimesheetQueue, type QueueTab } from "../../../components/timesheet-queue";
import {
  adminContractorsQuery,
  adminProjectsQuery,
  adminTimesheetsQuery,
} from "../../../lib/queries";

const searchSchema = z.object({
  status: z
    .enum(["SUBMITTED", "APPROVED", "CHANGES_REQUESTED", "ALL"])
    .default("SUBMITTED"),
  q: z.string().max(120).optional(),
  contractorId: z.string().optional(),
  projectId: z.string().optional(),
  clientId: z.string().optional(),
  /** Pins a single week; narrower than from/to, and what dashboard tiles link with. */
  weekStart: z.string().refine(isValidYmd).optional(),
  from: z.string().refine(isValidYmd).optional(),
  to: z.string().refine(isValidYmd).optional(),
  sort: z.enum(QUEUE_SORT_FIELDS).default("submittedAt"),
  dir: z.enum(SORT_DIRECTIONS).default("desc"),
  page: z.coerce.number().int().min(1).default(1),
});

export const Route = createFileRoute("/_authed/_staff/timesheets")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminTimesheetsQuery(deps)),
      context.queryClient.ensureQueryData(adminContractorsQuery),
      context.queryClient.ensureQueryData(adminProjectsQuery),
    ]),
  component: AdminTimesheetsPage,
});

function AdminTimesheetsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(adminTimesheetsQuery(search));
  const { data: contractorData } = useSuspenseQuery(adminContractorsQuery);
  const { data: projectData } = useSuspenseQuery(adminProjectsQuery);

  /**
   * Merge into the existing search rather than replacing it, and reset the page.
   *
   * Replacing would let one filter silently clear another. Resetting the page is
   * the same rule the tabs have always followed: page 4 of "Pending" is rarely
   * page 4 of anything else, and landing on an empty page reads as a bug.
   */
  const patch = (next: Partial<typeof search>) =>
    void navigate({ to: "/timesheets", search: { ...search, ...next, page: 1 } });

  return (
    <TimesheetQueue
      tab={search.status}
      counts={data.counts}
      rows={data.rows}
      pageInfo={data.pageInfo}
      filters={search}
      contractors={contractorData.contractors.map((contractor) => ({
        id: contractor.id,
        name: contractor.name,
      }))}
      projects={projectData.projects.map((project) => ({
        id: project.id,
        name: project.name,
        clientId: project.clientId,
        clientName: project.clientName,
      }))}
      onFilterChange={patch}
      onSortChange={(sort: QueueSortField) =>
        patch({
          // Third click on the same column returns to the default rather than
          // cycling forever between two orderings of a column you didn't want.
          sort,
          dir: search.sort === sort && search.dir === "desc" ? "asc" : "desc",
        })
      }
      onClearFilters={() =>
        void navigate({
          to: "/timesheets",
          search: { status: search.status, sort: "submittedAt", dir: "desc", page: 1 },
        })
      }
      onTabChange={(tab: QueueTab) => patch({ status: tab })}
      onPageChange={(next: number) =>
        void navigate({ to: "/timesheets", search: { ...search, page: next } })
      }
    />
  );
}
